const DB_NAME = 'clickorico-pos-offline-v1';
const DB_VERSION = 1;

const STORES = {
  meta: 'meta',
  catalog: 'catalog',
  outbox: 'outbox',
  acks: 'acks',
};

function openDb() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onerror = () => reject(req.error);
    req.onsuccess = () => resolve(req.result);
    req.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORES.meta)) {
        db.createObjectStore(STORES.meta, { keyPath: 'key' });
      }
      if (!db.objectStoreNames.contains(STORES.catalog)) {
        db.createObjectStore(STORES.catalog, { keyPath: 'commercantId' });
      }
      if (!db.objectStoreNames.contains(STORES.outbox)) {
        const outbox = db.createObjectStore(STORES.outbox, { keyPath: 'localId' });
        outbox.createIndex('byState', 'syncState', { unique: false });
        outbox.createIndex('bySequence', 'localSequence', { unique: false });
      }
      if (!db.objectStoreNames.contains(STORES.acks)) {
        db.createObjectStore(STORES.acks, { keyPath: 'clientOperationId' });
      }
    };
  });
}

async function txStore(storeName, mode, fn) {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, mode);
    const store = tx.objectStore(storeName);
    const result = fn(store);
    tx.oncomplete = () => resolve(result);
    tx.onerror = () => reject(tx.error);
  });
}

export async function getMeta(key) {
  return txStore(STORES.meta, 'readonly', (store) => {
    return new Promise((resolve, reject) => {
      const req = store.get(key);
      req.onsuccess = () => resolve(req.result?.value ?? null);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function setMeta(key, value) {
  return txStore(STORES.meta, 'readwrite', (store) => {
    store.put({ key, value });
  });
}

export async function nextLocalSequence(commercantId) {
  const seqKey = `seq:${commercantId}`;
  const current = (await getMeta(seqKey)) || 0;
  const next = current + 1;
  await setMeta(seqKey, next);
  return next;
}

export async function saveCatalogSnapshot(commercantId, snapshot) {
  return txStore(STORES.catalog, 'readwrite', (store) => {
    store.put({
      commercantId,
      snapshot,
      savedAt: new Date().toISOString(),
    });
  });
}

export async function getCatalogSnapshot(commercantId) {
  return txStore(STORES.catalog, 'readonly', (store) => {
    return new Promise((resolve, reject) => {
      const req = store.get(commercantId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function putOutboxEntry(entry) {
  return txStore(STORES.outbox, 'readwrite', (store) => {
    store.put(entry);
  });
}

export async function listOutboxEntries() {
  return txStore(STORES.outbox, 'readonly', (store) => {
    return new Promise((resolve, reject) => {
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function getOutboxEntry(localId) {
  return txStore(STORES.outbox, 'readonly', (store) => {
    return new Promise((resolve, reject) => {
      const req = store.get(localId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function saveAck(clientOperationId, ack) {
  return txStore(STORES.acks, 'readwrite', (store) => {
    store.put({ clientOperationId, ...ack });
  });
}

export async function getAck(clientOperationId) {
  return txStore(STORES.acks, 'readonly', (store) => {
    return new Promise((resolve, reject) => {
      const req = store.get(clientOperationId);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
  });
}

export async function clearOutboxForTests() {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(
      [STORES.outbox, STORES.acks, STORES.meta, STORES.catalog],
      'readwrite'
    );
    tx.objectStore(STORES.outbox).clear();
    tx.objectStore(STORES.acks).clear();
    tx.objectStore(STORES.meta).clear();
    tx.objectStore(STORES.catalog).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}
